import React, { useCallback } from "react";
import clsx from "clsx";
import SettingsIcon from "@material-ui/icons/Settings";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Popover from "@material-ui/core/Popover";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import WorkoutsPageState from "interfaces/WorkoutsPageState";
import {
  setIsCondensedView,
  setScheduledDatesOnly,
  setShouldScrollSync,
  setShowWeather
} from "store/actions/workoutsPage";
import { useDispatch, useSelector } from "react-redux";
import useStyles from "./styles";

const WorkoutSettings = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const dispatch = useDispatch();
  const { shouldScrollSync, isCondensedView, scheduledDatesOnly, showWeather } =
    useSelector(
      (state: { workoutsPage: WorkoutsPageState }) => state?.workoutsPage
    );
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const isCondensedViewEnabled = isCondensedView && matchesUpMd;

  const handleShouldScrollSyncChange = useCallback(
    ({ target: { checked } }) => dispatch(setShouldScrollSync(checked)),
    [dispatch]
  );

  const handleIsCondensedViewChange = useCallback(
    ({ target: { checked } }) => dispatch(setIsCondensedView(checked)),
    [dispatch]
  );

  const handleScheduledDatesOnlyChange = useCallback(
    ({ target: { checked } }) => dispatch(setScheduledDatesOnly(checked)),
    [dispatch]
  );

  const handleShowWeatherChange = useCallback(
    ({ target: { checked } }) => dispatch(setShowWeather(checked)),
    [dispatch]
  );

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const switches = [
    {
      title: "Weather Forecast",
      subtitle: "Shows the 7-Day-Forecast at the selected track.",
      value: isCondensedViewEnabled ? false : showWeather,
      handler: handleShowWeatherChange,
      isDisabled: isCondensedViewEnabled,
      className: isCondensedViewEnabled ? classes.listItemDeactivated : null
    },
    {
      title: "Scheduled Dates Only",
      subtitle: "Shows only the dates you currently have scheduled.",
      value: scheduledDatesOnly,
      handler: handleScheduledDatesOnlyChange
    },
    {
      title: "Lock Dates Vertically",
      subtitle:
        "Keeps all dates lined up vertically as you scroll left and right.",
      value: shouldScrollSync,
      handler: handleShouldScrollSyncChange,
      isDisabled: isCondensedViewEnabled
    },
    {
      title: "Condensed View",
      subtitle: "Show more horses and dates on the screen at one time.",
      value: isCondensedView,
      handler: handleIsCondensedViewChange,
      note: "Note: The 'Weather Forecast' and \"Lock Dates Vertically' settings are disabled when 'Condensed View' is set 'ON'",
      isHidden: !matchesUpMd
    }
  ];

  return (
    <section className={classes.root}>
      <Button
        variant="text"
        color="default"
        classes={{
          root: classes.buttonRoot,
          startIcon: clsx(classes.icon, {
            [classes.iconActive]: open
          })
        }}
        className={clsx(classes.button, {
          [classes.buttonActive]: open
        })}
        startIcon={<SettingsIcon />}
        onClick={handleOpen}
      >
        <span className={classes.buttonText}>Settings</span>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        classes={{
          paper: classes.paper
        }}
        elevation={24}
      >
        <Container className={classes.rootPopover}>
          <List disablePadding={true}>
            {switches.map(item =>
              item.isHidden ? (
                <React.Fragment key={item.title} />
              ) : (
                <ListItem
                  key={item.title}
                  disableGutters={true}
                  className={clsx(classes.listItem, item.className)}
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Grid item>
                          <div className={classes.text}>
                            <span className={classes.title}>{item.title}</span>
                            <span className={classes.subTitle}>
                              {item.subtitle}
                            </span>
                          </div>
                        </Grid>
                        <Grid item className={classes.switchContainer}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.value}
                                  onChange={item.handler}
                                  disabled={item.isDisabled}
                                  size={"small"}
                                />
                              }
                              label={item.value ? "On" : "Off"}
                              classes={{
                                label: classes.labelBase,
                                root: clsx(classes.label, {
                                  [classes.labelActive]: item.value
                                })
                              }}
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                    </Grid>
                    {item.note && (
                      <Grid item xs={12}>
                        <Typography className={classes.note}>
                          <b>Note:</b> The 'Weather Forecast' and "Lock Dates
                          Vertically' settings are disabled when 'Condensed
                          View' is set 'ON'
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </ListItem>
              )
            )}
          </List>
        </Container>
      </Popover>
    </section>
  );
};

export default WorkoutSettings;
